import { Component, ErrorInfo, lazy, ReactNode, Suspense, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { cleanText } from '@/utils/sanitize';
import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import { X, AlertCircle, Film, Edit as EditIcon, Eye, RefreshCw, Video, Loader2, Music, Sparkles, Save, Play, Image } from 'lucide-react';
import { courseService } from '@/services/course.service';
import { SceneFormData, sceneValidationSchema } from '@/validations/sceneValidation';
import VisualContentEditor, { VisualContentEditorHandle } from '@/components/content-editor/VisualContentEditor';
import { isHtmlEffectivelyEmpty } from '@/utils/htmlContent';
import { Scene, SceneData } from '@/types/course.types';
import ConfirmationModal from '@/components/ui/ConfirmationModal';
import SceneDataEditor from './SceneDataEditor';
import ScenePreviewCard from './ScenePreviewCard';

const RemotionPreviewPlayer = lazy(() => import('./remotion/RemotionPreviewPlayer'));

/** One scene is a single slide — keep its narration short. Matches the
 *  server-side cap in PedagogicalCourseService.MAX_SCENE_NARRATION_CHARS. */
const MAX_NARRATION_CHARS = 1500;

/** Catches dynamic-import chunk failures so the modal doesn't crash */
class ChunkErrorBoundary extends Component<{ fallback: ReactNode; children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };
  static getDerivedStateFromError(_: Error) { return { hasError: true }; }
  componentDidCatch(error: Error, info: ErrorInfo) {
    console.warn('RemotionPreviewPlayer chunk failed to load:', error.message, info);
  }
  render() { return this.state.hasError ? this.props.fallback : this.props.children; }
}

interface EditSceneModalProps {
  scene: Scene;
  contentItemId: string;
  courseLanguage?: string;
  theme?: string;
  videoRenderingMode?: 'animated' | 'static';
  onClose: () => void;
  onSuccess: () => void;
  /** Silently refresh the parent scene list (keeps the modal open) — used
   *  after AI HTML regenerate so the list/preview isn't stale until refresh. */
  onSceneUpdated?: () => void | Promise<void>;
}

type EditorMode = 'visual' | 'animation' | 'preview';

type ProcessingStep = 'saving' | 'audio' | 'video' | 'done' | 'error';

/**
 * Feedback cap for a single scene slide. A scene is one slide of on-screen text,
 * so instructions longer than this stop steering the model and start competing
 * with the slide's own content for the output budget.
 */
const FEEDBACK_MAX_CHARS = 500;
/** Show the counter in amber from here on, so the limit isn't a surprise. */
const FEEDBACK_WARN_CHARS = Math.round(FEEDBACK_MAX_CHARS * 0.9);

/**
 * Modal for editing scenes with dual-mode editor
 * Modes: Visual (contenteditable with full style preservation), Preview
 * Features: autosave, optimistic updates, draft caching, validation
 */
export default function EditSceneModal({ scene, contentItemId, courseLanguage = 'en-US', theme = 'educational', videoRenderingMode = 'animated', onClose, onSceneUpdated }: EditSceneModalProps) {
  const queryClient = useQueryClient();
  const isRTL = courseLanguage.startsWith('ar');
  const [isDirty, setIsDirty] = useState(false);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const has_scene_data = !!scene.scene_data;
  // Animated editor only when mode is 'animated' AND scene_data exists
  const use_animated_editor = videoRenderingMode === 'animated' && has_scene_data;
  const [editorMode, setEditorMode] = useState<EditorMode>(use_animated_editor ? 'animation' : 'visual');
  const [sceneVideoUrl, setSceneVideoUrl] = useState<string | undefined>(undefined);
  const [sceneThumbnailUrl, setSceneThumbnailUrl] = useState<string | undefined>(undefined);
  const [sceneAudioUrl, setSceneAudioUrl] = useState<string | undefined>(undefined);
  const [isGeneratingVideo, setIsGeneratingVideo] = useState(false);
  const [processingStep, setProcessingStep] = useState<ProcessingStep>('saving');
  const [processingError, setProcessingError] = useState<string>('');
  const [, setHasRegenerated] = useState(false);
  const [isAudioLoading, setIsAudioLoading] = useState(true);
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Imperative handle to read the visual editor's live content synchronously on
  // save — avoids depending on the 300ms debounced onChange (which loses a quick
  // type-then-save edit, most visibly on freshly-added scenes).
  const visualEditorRef = useRef<VisualContentEditorHandle>(null);
  // When true, ignore is_regenerating=false until we first see is_regenerating=true
  const waitingForRegenStartRef = useRef(false);
  const [sceneData, setSceneData] = useState<SceneData | undefined>(scene.scene_data || undefined);
  const [previewMode, setPreviewMode] = useState<'animated' | 'static'>('animated');

  // AI HTML generate/regenerate (same as content items). A freshly-added scene
  // still shows the starter placeholder → "Generate"; otherwise "Regenerate".
  const [showRegenDialog, setShowRegenDialog] = useState(false);
  const [regenFeedback, setRegenFeedback] = useState('');
  const [isRegeneratingContent, setIsRegeneratingContent] = useState(false);
  // Opt-in image generation for the regenerated slide. Off by default: it adds a
  // separate image call per slide, so it should be a deliberate choice.
  const [regenWithImages, setRegenWithImages] = useState(false);
  // While an AI HTML regen is running we own the is_regenerating flag, so the
  // video-regen poller below must stand down (it shares the same flag).
  const htmlRegenActiveRef = useRef(false);
  const _initialSceneHtml = (scene as any).html_content || '';
  const isFreshContent = isHtmlEffectivelyEmpty(_initialSceneHtml);
  const aiVerb = isFreshContent ? 'Generate' : 'Regenerate';
  const aiVerbing = isFreshContent ? 'Generating' : 'Regenerating';
  const feedbackChars = regenFeedback.length;
  const isFeedbackTooLong = feedbackChars > FEEDBACK_MAX_CHARS;

  // Poll every 5 seconds, also immediately on mount
  useEffect(() => {
    if (!scene._id) return;

    const checkStatus = async () => {
      // An AI HTML regen owns the is_regenerating flag while it runs — don't let
      // the video-regen poller hijack the UI during that time.
      if (htmlRegenActiveRef.current) return;
      try {
        const status = await courseService.getSceneStatus(scene._id!);

        if (status.is_regenerating) {
          // Regenerating — show inline loader in header
          waitingForRegenStartRef.current = false;
          setIsGeneratingVideo(true);
          setProcessingStep('saving');
          setIsAudioLoading(true);
          setIsVideoLoading(true);
        } else if (waitingForRegenStartRef.current) {
          // We just clicked regenerate but server hasn't started yet — skip
          return;
        } else if (isGeneratingVideo) {
          // Was generating, now done — bind values from status API
          if (status.error_message) {
            setProcessingStep('error');
            setProcessingError(status.error_message);
          } else {
            setProcessingStep('done');
            setSceneAudioUrl(status.media_urls?.s3_audio_url);
            setSceneVideoUrl(status.media_urls?.s3_video_url);
            setSceneThumbnailUrl(status.media_urls?.thumbnail_url);
            setHasRegenerated(true);
            setIsDirty(false);
            queryClient.invalidateQueries({ queryKey: ['contentItemScenes', contentItemId] });
          }
          setIsGeneratingVideo(false);
          setIsAudioLoading(!!status.media_urls?.s3_audio_url);
          setIsVideoLoading(!!status.media_urls?.s3_video_url);
        } else {
          // Not regenerating — bind URLs from status API
          setSceneAudioUrl(status.media_urls?.s3_audio_url);
          setSceneVideoUrl(status.media_urls?.s3_video_url);
          setSceneThumbnailUrl(status.media_urls?.thumbnail_url);
          if (isInitialLoad) {
            // Keep loading true — onCanPlay/onLoad will clear them
            setIsAudioLoading(!!status.media_urls?.s3_audio_url);
            setIsVideoLoading(!!status.media_urls?.s3_video_url);
            setIsInitialLoad(false);
          }
        }
      } catch (_err) {
        // Polling error — keep retrying
      }
    };

    checkStatus();
    pollingRef.current = setInterval(checkStatus, 5000);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [scene._id, contentItemId, queryClient, isGeneratingVideo, isInitialLoad]);

  // Initialize form with current scene data
  const {
    register,
    formState: { errors },
    setValue,
    setError,
    clearErrors,
    watch,
  } = useForm<SceneFormData>({
    resolver: yupResolver(sceneValidationSchema) as any,
    defaultValues: {
      title: scene.title || '',
      html_content: (scene as any).html_content || '',
      narration: scene.narration || '',
      visual_description: scene.visual_description || '',
      media_urls: {
        s3_audio_url: scene.media_urls?.s3_audio_url || '',
        s3_screenshot_url: (scene.media_urls as any)?.s3_screenshot_url || '',
      },
    },
  });

  // Watch all form fields to detect changes
  const formValues = watch();

  // Track dirty state
  useEffect(() => {
    const subscription = watch(() => {
      setIsDirty(true);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  // Unsaved changes warning
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const [isSavingDraft, setIsSavingDraft] = useState(false);

  /**
   * Decide what HTML to persist, reading the editor's live DOM so the newest edit
   * is captured even if the 300ms debounced onChange hasn't fired yet (the
   * "won't save on new scenes" bug).
   *
   * An EMPTY editor is an intentional clear and is saved as empty. Only a MISSING
   * editor (not mounted — animation editor showing, or preview mode) falls back to
   * the form value. Treating '' as "nothing to save" is what previously restored
   * the old slide and made it look like the content could not be cleared.
   */
  const resolveHtmlToSave = (formHtml?: string): string => {
    const liveHtml = visualEditorRef.current?.getContent();
    return liveHtml !== undefined ? liveHtml : (formHtml ?? '');
  };

  // Generate the narration from the slide HTML (same flow as the html_with_audio
  // content editor). The backend uses the course's language config and caps the
  // result at MAX_NARRATION_CHARS.
  const [isGeneratingNarration, setIsGeneratingNarration] = useState(false);

  const handleGenerateNarration = async () => {
    if (!scene._id) return;

    // No empty-HTML guard: a fresh scene has only the starter placeholder, and
    // the backend narrates from the lesson/module context in that case.
    const html = resolveHtmlToSave(watch('html_content'));

    setIsGeneratingNarration(true);
    clearErrors('narration');
    try {
      const result = await courseService.generateSceneNarration(scene._id, html, {
        language_code: courseLanguage,
      });
      setValue('narration', result.narration, { shouldValidate: true, shouldDirty: true });
    } catch (error: any) {
      setError('narration', { type: 'manual', message: error?.message || 'Failed to generate narration' });
    } finally {
      setIsGeneratingNarration(false);
    }
  };

  // Save Draft: saves fields only, no TTS/video regeneration
  const handleSaveDraft = async () => {
    if (!scene._id) return;

    const currentData = watch();
    const htmlToSave = resolveHtmlToSave(currentData.html_content);
    if (visualEditorRef.current?.getContent() !== undefined) {
      setValue('html_content', htmlToSave, { shouldValidate: true, shouldDirty: false });
    }
    setIsSavingDraft(true);

    try {
      await courseService.updateScene(scene._id!, {
        title: cleanText(currentData.title),
        html_content: htmlToSave,
        narration: cleanText(currentData.narration ?? ''),
        ...(sceneData ? { scene_data: sceneData } : {}),
      });
      setIsDirty(false);
      await queryClient.invalidateQueries({ queryKey: ['contentItemScenes', contentItemId] });
      // Parent list reads from its own state — refresh it so the edited HTML/
      // narration is reflected immediately (not only after a full page refresh).
      await onSceneUpdated?.();
    } catch (err: any) {
      setProcessingStep('error');
      setProcessingError(err.message || 'Failed to save draft');
    } finally {
      setIsSavingDraft(false);
    }
  };

  // AI generate/regenerate the scene's HTML slide (mirrors content items).
  // Fire-and-forget: the backend starts generation and returns immediately; we
  // poll getSceneStatus until it finishes, then load the fresh HTML. This avoids
  // the request timing out on long generations.
  const handleRegenerateSceneContent = async () => {
    if (!scene._id) return;
    // Guarded here too — the button is disabled, but a paste can still land here.
    if (isFeedbackTooLong) return;
    const sceneId = scene._id;
    setIsRegeneratingContent(true);
    htmlRegenActiveRef.current = true;
    try {
      await courseService.regenerateSceneHtml(sceneId, {
        feedback: regenFeedback.trim() || undefined,
        regenerate_images: regenWithImages,
      });

      // Poll until the background regeneration completes (is_regenerating=false).
      const newHtml = await new Promise<string | undefined>((resolve, reject) => {
        const startedAt = Date.now();
        const tick = async () => {
          try {
            const status = await courseService.getSceneStatus(sceneId);
            if (!status.is_regenerating) {
              if (status.error_message) return reject(new Error(status.error_message));
              return resolve(status.html_content);
            }
          } catch {
            // transient poll error — keep trying
          }
          if (Date.now() - startedAt > 5 * 60 * 1000) {
            return reject(new Error('Regeneration timed out. Please try again.'));
          }
          setTimeout(tick, 3000);
        };
        setTimeout(tick, 2500);
      });

      if (newHtml !== undefined) {
        setValue('html_content', newHtml, { shouldValidate: true, shouldDirty: false });
      }
      setIsDirty(false);
      setShowRegenDialog(false);
      setRegenFeedback('');
      setRegenWithImages(false);
      await queryClient.invalidateQueries({ queryKey: ['contentItemScenes', contentItemId] });
      // Parent list reads from its own state — refresh so it isn't stale.
      await onSceneUpdated?.();
    } catch (error: any) {
      setShowRegenDialog(false);
      setProcessingStep('error');
      setProcessingError(error?.response?.data?.error?.error_description || error?.message || 'Failed to regenerate scene content');
    } finally {
      setIsRegeneratingContent(false);
      htmlRegenActiveRef.current = false;
    }
  };

  // Handle close with unsaved changes check
  const handleClose = () => {
    if (isDirty) {
      setShowUnsavedWarning(true);
    } else {
      onClose();
    }
  };

  const confirmClose = () => {
    onClose();
  };

  // Regenerate & Save: fires API call, polling handles the result
  const handleRegenerateAndSave = () => {
    if (!scene._id) return;

    const currentData = watch();
    // Capture the editor's live content (see resolveHtmlToSave) so the rendered
    // video uses the newest edit, not stale debounced HTML.
    const htmlToSave = resolveHtmlToSave(currentData.html_content);

    // Narration is required to generate audio → video.
    if (!currentData.narration || currentData.narration.trim().length === 0) {
      setError('narration', {
        type: 'required',
        message: 'Narration text is required to generate the scene audio and video.',
      });
      return;
    }

    // Tell polling to wait until server confirms is_regenerating=true
    waitingForRegenStartRef.current = true;

    setIsGeneratingVideo(true);
    setProcessingStep('saving');
    setProcessingError('');
    setIsDirty(false);

    // Fire and forget — polling will detect completion via getSceneStatus
    courseService.regenerateSceneVideo(scene._id!, {
      title: cleanText(currentData.title),
      html_content: htmlToSave,
      narration: cleanText(currentData.narration ?? ''),
      scene_data: sceneData,
    }).catch(() => {
      // Error handling is done via polling (error_message field)
    });
  };

  return (
    <>
      {/* Main Modal */}
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-1 sm:p-2 md:p-[2vh]" role="dialog" aria-modal="true" dir="ltr">
        {/* Protect modal UI from dark-mode content styles leaking */}
        <style dangerouslySetInnerHTML={{ __html: `
          .scene-edit-modal .modal-sidebar, .scene-edit-modal .modal-sidebar * { direction: ltr !important; text-align: left !important; }
          .scene-edit-modal .modal-sidebar, .scene-edit-modal .modal-sidebar *:not(.text-white, .text-white *, [class*="text-brand-"], [class*="text-brand-"] *, [class*="text-primary-"], [class*="text-primary-"] *) { color: #111827 !important; }
          .scene-edit-modal .modal-sidebar input[dir="rtl"], .scene-edit-modal .modal-sidebar textarea[dir="rtl"] { direction: rtl !important; text-align: right !important; }
          .scene-edit-modal .modal-sidebar .text-center, .scene-edit-modal .modal-sidebar .text-center * { text-align: center !important; }
          .scene-edit-modal .modal-sidebar input, .scene-edit-modal .modal-sidebar textarea, .scene-edit-modal .modal-sidebar select { color: #111827 !important; background-color: #fff !important; }
          .scene-edit-modal .modal-sidebar label { color: #111827 !important; }
          .scene-edit-modal .modal-header, .scene-edit-modal .modal-header *:not(.text-white, .text-white *, [class*="text-brand-"], [class*="text-brand-"] *, [class*="text-primary-"], [class*="text-primary-"] *) { color: #111827 !important; }
          .scene-edit-modal .modal-header .text-white { color: #fff !important; }
          .scene-edit-modal .modal-header .text-purple-600 { color: #9333ea !important; }
          .scene-edit-modal .modal-header .text-gray-500 { color: #6b7280 !important; }
          /* ===== EDITOR: SHOW THE WHOLE SLIDE =====
             A scene's own CSS pins the slide to a 1920x1080 frame and CLIPS anything
             past it (.slide/.board-frame height + overflow:hidden, .board-surface
             overflow:hidden). That is correct for rendering, but inside the editor it
             makes the tail of a long slide unreachable — a 4th bullet sits below the
             fold and no amount of scrolling brings it into view, because the slide
             itself is hiding it, not the scroll container.
             Scoped to .scene-editor-wrap, which only exists in EDIT mode, so Preview
             still shows the true clipped frame the learner will see. */
          .scene-edit-modal .scene-editor-wrap .editor-content-preview,
          .scene-edit-modal .scene-editor-wrap .slide,
          .scene-edit-modal .scene-editor-wrap .board-frame,
          .scene-edit-modal .scene-editor-wrap .board-surface {
            height: auto !important;
            min-height: 0 !important;
            max-height: none !important;
            overflow: visible !important;
          }
          /* Vertical centring collapses to the top once the height is free, so the
             content does not float away from the heading while editing. */
          .scene-edit-modal .scene-editor-wrap .slide {
            display: block !important;
            align-items: flex-start !important;
            justify-content: flex-start !important;
          }
          /* Anything the slide clipped internally (long lists, panels) becomes visible. */
          .scene-edit-modal .scene-editor-wrap .board-surface > * {
            overflow: visible !important;
          }
          .scene-edit-modal .modal-footer, .scene-edit-modal .modal-footer *:not(.text-white, .text-white *, [class*="text-brand-"], [class*="text-brand-"] *, [class*="text-primary-"], [class*="text-primary-"] *) { color: #111827 !important; }
          .scene-edit-modal .modal-footer .text-white { color: #fff !important; }
          .scene-edit-modal .modal-footer .text-orange-600 { color: #ea580c !important; }
          .scene-edit-modal .modal-footer .text-gray-700 { color: #374151 !important; }
        ` }} />
        <div className="scene-edit-modal bg-white w-full h-full rounded-lg sm:rounded-xl shadow-2xl overflow-y-auto overflow-x-hidden scrollbar-hide flex flex-col">
          {/* Header - Compact */}
          <div className="modal-header flex items-center justify-between px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200 bg-white">
            <div className="min-w-0">
              <h2 className="text-base sm:text-lg font-bold text-gray-900 flex items-center gap-2">
                <Film className="w-4 h-4 sm:w-5 sm:h-5 text-brand-purple-600 flex-shrink-0" />
                <span className="truncate">Edit Scene</span>
              </h2>
              <span className="text-xs text-gray-500">
                {scene.scene_number ? `Scene #${scene.scene_number}` : 'Scene editing'}
              </span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
              {/* AI generate/regenerate the scene's HTML slide (visual/HTML scenes only) */}
              {!use_animated_editor && !isGeneratingVideo && (
                <button
                  type="button"
                  onClick={() => setShowRegenDialog(true)}
                  disabled={isRegeneratingContent || editorMode === 'preview'}
                  title={`${aiVerb} this scene's content with AI`}
                  className="px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 bg-gradient-to-r from-brand-purple-500 to-brand-indigo-600 text-white hover:from-brand-purple-600 hover:to-brand-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {isRegeneratingContent ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                  {aiVerb} with AI
                </button>
              )}
              {/* Mode Toggle in Header / Regenerating indicator */}
              {isGeneratingVideo ? (
                <div className="flex items-center gap-1.5 bg-green-50 text-green-600 rounded-lg px-3 py-1.5">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span className="text-xs font-medium">{sceneVideoUrl ? 'Regenerating...' : 'Generating...'}</span>
                </div>
              ) : (
                <div className="flex bg-gray-100 rounded-lg p-0.5">
                  {use_animated_editor ? (
                    <>
                      <button
                        type="button"
                        onClick={() => setEditorMode('animation')}
                        className={`px-2 sm:px-2.5 py-1 rounded text-xs font-medium transition-all flex items-center gap-1 ${
                          editorMode === 'animation'
                            ? 'bg-white text-brand-purple-600 shadow-sm'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        <Sparkles className="w-3 h-3" />
                        <span className="hidden xs:inline">Edit</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditorMode('preview')}
                        className={`px-2 sm:px-2.5 py-1 rounded text-xs font-medium transition-all flex items-center gap-1 ${
                          editorMode === 'preview'
                            ? 'bg-white text-brand-purple-600 shadow-sm'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        <Eye className="w-3 h-3" />
                        <span className="hidden xs:inline">Preview</span>
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => setEditorMode('visual')}
                        className={`px-2 sm:px-2.5 py-1 rounded text-xs font-medium transition-all flex items-center gap-1 ${
                          editorMode === 'visual'
                            ? 'bg-white text-brand-purple-600 shadow-sm'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        <EditIcon className="w-3 h-3" />
                        <span className="hidden xs:inline">Edit</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditorMode('preview')}
                        className={`px-2 sm:px-2.5 py-1 rounded text-xs font-medium transition-all flex items-center gap-1 ${
                          editorMode === 'preview'
                            ? 'bg-white text-brand-purple-600 shadow-sm'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        <Eye className="w-3 h-3" />
                        <span className="hidden xs:inline">Preview</span>
                      </button>
                    </>
                  )}
                </div>
              )}
              <button onClick={handleClose} className="w-7 h-7 flex items-center justify-center hover:bg-gray-100 rounded transition-colors" type="button">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          </div>

          {/* Form Content - Split Layout */}
          <form onSubmit={(e) => e.preventDefault()} className="flex-1 overflow-hidden flex flex-col md:flex-row">
            {/* Editor Section - Left side */}
            <div className="flex-1 flex flex-col min-w-0 md:border-r border-gray-200 order-2 md:order-1">

              {/* Validation Error Banner */}
              {errors.html_content && (
                <div className="sticky top-0 z-30 bg-red-50 border-b-2 border-red-300 px-3 sm:px-6 py-2 sm:py-3">
                  <div className="flex items-start gap-2 sm:gap-3">
                    <div className="flex-shrink-0 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center mt-0.5">
                      <AlertCircle className="w-3 h-3 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-red-900">Content Validation Error</p>
                      <p className="text-xs text-red-700 mt-1">{errors.html_content.message}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Editor Area */}
              <div className="flex-1 overflow-y-auto overflow-x-hidden p-3 sm:p-6 bg-gray-50 scrollbar-hide">
                {editorMode === 'visual' && !use_animated_editor && (
                  <div className="max-w-full scene-editor-wrap">
                    <VisualContentEditor
                      ref={visualEditorRef}
                      initialContent={formValues.html_content}
                      onChange={(html) => setValue('html_content', html, { shouldValidate: true, shouldDirty: true })}
                      onSave={() => handleRegenerateAndSave()}
                      hideVideoInsert
                      hideAudioInsert
                      hideKnowledgeCheck
                      isRTL={isRTL}
                      className="[&_.editor-content-preview]:!max-h-none [&_.editor-content-preview]:!overflow-visible [&_.editor-content-preview]:!overflow-x-hidden [&_.editor-content-preview]:!max-w-full [&_.editor-content-preview_*]:!max-w-full [&_.editor-content-preview_img]:!h-auto [&_.editor-content-preview_table]:!w-full [&_.editor-content-preview_table]:!table-fixed [&_.editor-content-preview]:[zoom:0.75]"
                    />
                  </div>
                )}

                {editorMode === 'animation' && (
                  <div className="flex flex-col lg:flex-row gap-4 h-full">
                    {/* Left: SceneData form */}
                    <div className="lg:w-1/2 bg-white rounded-xl p-4 sm:p-5 overflow-y-auto scrollbar-hide">
                      <div className="mb-3">
                        <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-brand-purple-600" />
                          Animation Layout
                        </h3>
                        <p className="text-xs text-gray-500 mt-1">
                          Edit fields below — the preview updates live.
                        </p>
                      </div>
                      <SceneDataEditor
                        value={sceneData}
                        onChange={(updated) => { setSceneData(updated); setIsDirty(true); }}
                        readOnly={false}
                      />
                    </div>
                    {/* Right: Live preview */}
                    <div className="lg:w-1/2 flex flex-col gap-3">
                      <div className="flex items-center justify-between px-1">
                        <div className="flex items-center gap-2">
                          <Eye className="w-3.5 h-3.5 text-gray-400" />
                          <span className="text-xs font-medium text-gray-500">Live Preview</span>
                        </div>
                        <div className="flex bg-gray-100 rounded-md p-0.5">
                          <button
                            type="button"
                            onClick={() => setPreviewMode('animated')}
                            className={`px-2 py-0.5 rounded text-[10px] font-medium transition-all flex items-center gap-1 ${
                              previewMode === 'animated'
                                ? 'bg-white text-brand-purple-600 shadow-sm'
                                : 'text-gray-400 hover:text-gray-600'
                            }`}
                            title="Animated preview (Remotion)"
                          >
                            <Play className="w-2.5 h-2.5" />
                            Animated
                          </button>
                          <button
                            type="button"
                            onClick={() => setPreviewMode('static')}
                            className={`px-2 py-0.5 rounded text-[10px] font-medium transition-all flex items-center gap-1 ${
                              previewMode === 'static'
                                ? 'bg-white text-brand-purple-600 shadow-sm'
                                : 'text-gray-400 hover:text-gray-600'
                            }`}
                            title="Static preview"
                          >
                            <Image className="w-2.5 h-2.5" />
                            Static
                          </button>
                        </div>
                      </div>
                      <div className="flex-1 flex items-start justify-center">
                        <div className="w-full max-w-[640px]">
                          {previewMode === 'animated' ? (
                            <ChunkErrorBoundary fallback={
                              <div className="w-full aspect-video bg-gray-900 rounded-lg flex items-center justify-center">
                                <div className="flex flex-col items-center gap-2 text-center px-4">
                                  <AlertCircle className="w-6 h-6 text-amber-400" />
                                  <span className="text-xs text-gray-400">Animation preview unavailable — showing static preview</span>
                                </div>
                                <ScenePreviewCard data={sceneData || { layout_type: 'content', heading: '' }} />
                              </div>
                            }>
                              <Suspense fallback={
                                <div className="w-full aspect-video bg-gray-900 rounded-lg flex items-center justify-center">
                                  <div className="flex flex-col items-center gap-2">
                                    <Loader2 className="w-6 h-6 text-brand-purple-400 animate-spin" />
                                    <span className="text-xs text-gray-400">Loading Remotion player...</span>
                                  </div>
                                </div>
                              }>
                                <RemotionPreviewPlayer
                                  scene_data={sceneData || { layout_type: 'content', heading: '' }}
                                  theme_name={theme}
                                  duration_seconds={8}
                                  loop
                                  autoPlay
                                  controls
                                />
                              </Suspense>
                            </ChunkErrorBoundary>
                          ) : (
                            <ScenePreviewCard data={sceneData || { layout_type: 'content', heading: '' }} />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {editorMode === 'preview' && (
                  <div className="bg-black rounded-lg max-w-full flex items-center justify-center min-h-[500px] h-full">
                    {(isInitialLoad || sceneVideoUrl) ? (
                      <>
                        {(isInitialLoad || isVideoLoading) && (
                          <div className="flex flex-col items-center justify-center text-gray-400 gap-2">
                            <Loader2 className="w-8 h-8 text-brand-purple-400 animate-spin" />
                            <p className="text-sm text-gray-400">Loading video...</p>
                          </div>
                        )}
                        {sceneVideoUrl && (
                          <video
                            src={sceneVideoUrl}
                            controls
                            poster={sceneThumbnailUrl}
                            className={`w-full h-full rounded-lg ${(isInitialLoad || isVideoLoading) ? 'hidden' : ''}`}
                            style={{ maxHeight: '100%', objectFit: 'contain' }}
                            onCanPlay={() => setIsVideoLoading(false)}
                            onError={() => setIsVideoLoading(false)}
                          />
                        )}
                      </>
                    ) : (
                      <div className="flex flex-col items-center justify-center text-gray-400 gap-2">
                        <Film className="w-10 h-10" />
                        <p className="text-sm">No video available. Regenerate to create the video.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar - Right side */}
            <div dir="ltr" className="modal-sidebar w-full md:w-[350px] lg:w-[400px] flex-shrink-0 flex flex-col bg-white border-b md:border-b-0 md:border-l border-gray-200 order-1 md:order-2">
              <div className="flex-1 overflow-y-auto p-3 sm:p-4 md:p-6 space-y-3 sm:space-y-4 md:space-y-6 max-h-[35vh] md:max-h-none scrollbar-hide">
                {/* Title */}
                <div>
                  <label className="block text-xs sm:text-sm font-bold text-gray-900 mb-1.5 sm:mb-2">
                    Scene Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    {...register('title')}
                    dir={isRTL ? 'rtl' : 'ltr'}
                    readOnly={editorMode === 'preview'}
                    className={`w-full px-3 sm:px-4 py-2 sm:py-3 border-2 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-purple-500 transition-all ${
                      errors.title ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter scene title"
                  />
                  {errors.title && <p className="text-xs text-red-600 mt-1">{errors.title.message}</p>}
                </div>

                {/* Visual Description */}
                {/* <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">Visual Description</label>
                  <textarea
                    {...register('visual_description')}
                    rows={2}
                    readOnly={editorMode === 'preview'}
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-purple-500 resize-none transition-all scrollbar-hide ${
                      errors.visual_description ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Describe what is visually displayed in this scene"
                  />
                  {errors.visual_description && (
                    <p className="text-xs text-red-600 mt-1">{errors.visual_description.message}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">{formValues.visual_description?.length || 0} / 500 characters</p>
                </div> */}

                {/* Narration */}
                <div>
                  <div className="flex items-center justify-between mb-1.5 sm:mb-2">
                    <label className="block text-xs sm:text-sm font-bold text-gray-900">Narration Text</label>
                    {/* Derives the narration from this slide's HTML, in the
                        course language — same flow as the lesson editor. */}
                    <button
                      type="button"
                      onClick={handleGenerateNarration}
                      disabled={isGeneratingNarration || editorMode === 'preview'}
                      className="px-2.5 py-1 bg-brand-indigo-600 text-white rounded-md hover:bg-brand-indigo-700 transition-colors disabled:opacity-50 text-[11px] font-medium flex items-center gap-1"
                    >
                      {isGeneratingNarration ? (
                        <>
                          <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Analyzing...
                        </>
                      ) : (
                        'Generate from HTML'
                      )}
                    </button>
                  </div>
                  <textarea
                    {...register('narration')}
                    rows={4}
                    maxLength={MAX_NARRATION_CHARS}
                    dir={isRTL ? 'rtl' : 'ltr'}
                    readOnly={editorMode === 'preview'}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 border-2 border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-purple-500 resize-none transition-all scrollbar-hide"
                    placeholder="Enter narration or click 'Generate from HTML' for AI-powered narration..."
                  />
                  {errors.narration && <p className="text-xs text-red-600 mt-1">{errors.narration.message}</p>}
                  <p className={`text-xs mt-1 ${(formValues.narration?.length || 0) >= MAX_NARRATION_CHARS ? 'text-amber-600 font-medium' : 'text-gray-500'}`}>
                    {formValues.narration?.length || 0} / {MAX_NARRATION_CHARS} characters
                  </p>
                </div>

                {/* Media Assets */}
                {/* <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">Media Assets</label>
                  <div className="space-y-3">
                    <MediaUploader
                      label="Audio File"
                      description="Scene narration audio"
                      currentUrl={formValues.media_urls?.s3_audio_url || undefined}
                      onUploadComplete={(url) =>
                        setValue('media_urls.s3_audio_url', url, { shouldValidate: true, shouldDirty: true })
                      }
                      accept="audio/*"
                      mediaType="audio"
                      maxSize={50}
                    />

                    <MediaUploader
                      label="Screenshot"
                      description="Scene visual screenshot"
                      currentUrl={formValues.media_urls?.s3_screenshot_url || undefined}
                      onUploadComplete={(url) =>
                        setValue('media_urls.s3_screenshot_url', url, { shouldValidate: true, shouldDirty: true })
                      }
                      accept="image/*"
                      mediaType="image"
                      maxSize={10}
                    />
                  </div>
                </div> */}

                {/* Scene Audio */}
                <div>
                  <label className="block text-xs sm:text-sm font-bold text-gray-900 mb-1.5 sm:mb-2 flex items-center gap-2">
                    <Music className="w-4 h-4 text-brand-purple-600" />
                    Scene Audio
                  </label>

                  {(isInitialLoad || sceneAudioUrl) ? (
                    <div className="space-y-2">
                      {isAudioLoading && (
                        <div className="w-full h-10 rounded-lg bg-gray-100 flex items-center justify-center gap-2">
                          <Loader2 className="w-4 h-4 text-brand-purple-600 animate-spin" />
                          <span className="text-xs text-gray-500">Loading audio...</span>
                        </div>
                      )}
                      <audio
                        controls
                        className={`w-full h-10 rounded-lg ${isAudioLoading ? 'hidden' : ''}`}
                        src={sceneAudioUrl}
                        key={sceneAudioUrl}
                        onCanPlay={() => setIsAudioLoading(false)}
                        onError={() => setIsAudioLoading(false)}
                      >
                        Your browser does not support audio playback.
                      </audio>
                      <p className="text-xs text-green-600">Audio generated from narration</p>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-gray-200 rounded-xl p-3 text-center">
                      <p className="text-xs text-gray-500">No audio yet. Click Regenerate to generate from narration.</p>
                    </div>
                  )}
                </div>

                {/* Scene Video */}
                <div>
                  <label className="block text-xs sm:text-sm font-bold text-gray-900 mb-1.5 sm:mb-2 flex items-center gap-2">
                    <Video className="w-4 h-4 text-brand-purple-600" />
                    Scene Video
                  </label>

                  {(isInitialLoad || sceneVideoUrl) ? (
                    <div className="space-y-3">
                      {isVideoLoading && (
                        <div className="w-full aspect-video rounded-xl border-2 border-gray-200 bg-gray-100 flex flex-col items-center justify-center gap-2">
                          <Loader2 className="w-6 h-6 text-brand-purple-600 animate-spin" />
                          <span className="text-xs text-gray-500">Loading video...</span>
                        </div>
                      )}
                      <video
                        controls
                        className={`w-full rounded-xl border-2 border-gray-200 ${isVideoLoading ? 'hidden' : ''}`}
                        src={sceneVideoUrl}
                        poster={sceneThumbnailUrl}
                        onCanPlay={() => setIsVideoLoading(false)}
                        onError={() => setIsVideoLoading(false)}
                      >
                        Your browser does not support video playback.
                      </video>
                      {/* Regenerate & Save button is in the footer */}
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 text-center">
                      <div className="w-10 h-10 bg-brand-purple-100 rounded-full flex items-center justify-center mx-auto mb-2">
                        <Video className="w-5 h-5 text-brand-purple-500" />
                      </div>
                      <p className="text-xs font-medium text-gray-700 mb-1">No video generated yet</p>
                      <p className="text-xs text-gray-500 mb-3">Generate from HTML content and narration</p>
                      <button
                        type="button"
                        onClick={handleRegenerateAndSave}
                        disabled={isGeneratingVideo}
                        className={`w-full px-4 py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-2 transition-colors ${
                          isGeneratingVideo
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : 'bg-brand-purple-600 text-white hover:bg-brand-purple-700'
                        }`}
                        title="Save changes, generate audio from narration, and render video"
                      >
                        {isGeneratingVideo ? (
                          <>
                            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Film className="w-3.5 h-3.5" />
                            Generate Scene Video
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </form>

          {/* Footer - Compact */}
          <div className="modal-footer flex items-center justify-between gap-2 sm:gap-3 px-3 sm:px-6 py-3 sm:py-4 border-t border-gray-200 bg-white shadow-sm">
            <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm min-w-0">
              {isDirty && (
                <>
                  <AlertCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-orange-500 flex-shrink-0" />
                  <span className="font-medium text-orange-600 truncate">Unsaved changes</span>
                </>
              )}
            </div>
            <div className="flex gap-2 sm:gap-3 flex-shrink-0">
              <button
                type="button"
                onClick={handleClose}
                disabled={isGeneratingVideo || isSavingDraft}
                className="px-3 sm:px-5 py-1.5 sm:py-2 text-sm border border-gray-300 text-gray-700 bg-white rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              {/* Save Draft — saves fields only, no video pipeline */}
              <button
                type="button"
                onClick={handleSaveDraft}
                disabled={isGeneratingVideo || isSavingDraft || !isDirty}
                title={!isDirty ? 'Make changes to enable' : 'Save text and animation data without regenerating video'}
                className="px-3 sm:px-5 py-1.5 sm:py-2 text-sm border-2 border-brand-purple-300 text-brand-purple-700 bg-brand-purple-50 rounded-lg hover:bg-brand-purple-100 transition-colors disabled:opacity-50 flex items-center gap-1.5 font-medium"
              >
                {isSavingDraft ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span className="hidden sm:inline">Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Save Draft</span>
                    <span className="sm:hidden">Save</span>
                  </>
                )}
              </button>
              {/* Regenerate — saves + triggers TTS + video render. Only shown once
                  a video exists; before that the user generates it via the
                  "Generate Scene Video" button (no Regenerate button yet). */}
              {sceneVideoUrl && (
                <button
                  type="button"
                  onClick={handleRegenerateAndSave}
                  disabled={isGeneratingVideo || isSavingDraft}
                  title="Save changes, regenerate audio, and render video"
                  className="px-4 sm:px-6 py-1.5 sm:py-2 text-sm bg-brand-purple-600 text-white rounded-lg hover:bg-brand-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 sm:gap-2 font-semibold shadow-sm"
                >
                  {isGeneratingVideo ? (
                    <>
                      <RefreshCw className="w-3.5 h-3.5 sm:w-4 sm:h-4 animate-spin" />
                      <span className="hidden sm:inline">Regenerating...</span>
                      <span className="sm:hidden">...</span>
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      <span className="hidden sm:inline">Regenerate Scene</span>
                      <span className="sm:hidden">Regen</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* AI Generate/Regenerate scene HTML — feedback dialog / progress */}
      {showRegenDialog && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" dir="ltr">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            {isRegeneratingContent ? (
              <div className="px-8 py-10 flex flex-col items-center text-center">
                <div className="relative w-16 h-16 mb-5">
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-brand-purple-500 to-brand-indigo-600 animate-ping opacity-30" />
                  <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-brand-purple-500 to-brand-indigo-600 flex items-center justify-center shadow-lg">
                    <Sparkles className="w-7 h-7 text-white animate-pulse" />
                  </div>
                </div>
                <h3 className="text-base font-bold text-gray-900">{aiVerbing} your scene…</h3>
                <p className="text-sm text-gray-500 mt-1.5 max-w-xs">
                  The AI is {isFreshContent ? 'building' : 'rebuilding'} this scene slide using your title, module context{regenFeedback.trim() ? ' and feedback' : ''}. This usually takes 20–60 seconds.
                </p>
                <p className="text-[11px] text-gray-400 mt-4">Please keep this window open.</p>
              </div>
            ) : (
              <>
                <div className="flex items-start gap-3 px-6 pt-6 pb-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-brand-purple-500 to-brand-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-bold text-gray-900">{aiVerb} with AI</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {isFreshContent
                        ? "Generates this scene's slide HTML from the title and module context. Add feedback below to steer it."
                        : "Rebuilds this scene's slide HTML. Your current content is used as context, so an incomplete slide gets completed — not replaced blindly."}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setShowRegenDialog(false); setRegenFeedback(''); setRegenWithImages(false); }}
                    className="w-7 h-7 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                  >
                    <X className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
                <div className="px-6 pb-2">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-semibold text-gray-800">
                      Feedback / instructions <span className="font-normal text-gray-400">(optional)</span>
                    </label>
                    <span
                      className={`text-[11px] font-medium tabular-nums ${
                        isFeedbackTooLong
                          ? 'text-red-600'
                          : feedbackChars >= FEEDBACK_WARN_CHARS
                            ? 'text-amber-600'
                            : 'text-gray-400'
                      }`}
                    >
                      {feedbackChars}/{FEEDBACK_MAX_CHARS}
                    </span>
                  </div>
                  <textarea
                    value={regenFeedback}
                    onChange={(e) => setRegenFeedback(e.target.value)}
                    rows={4}
                    maxLength={FEEDBACK_MAX_CHARS}
                    autoFocus
                    placeholder="e.g. Turn this into a bullet summary of the key points."
                    className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:ring-2 focus:border-transparent resize-none !text-gray-900 !bg-white placeholder:!text-gray-400 ${
                      isFeedbackTooLong
                        ? 'border-red-400 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-brand-purple-500'
                    }`}
                  />
                  {isFeedbackTooLong && (
                    <p className="text-[11px] text-red-600 mt-1.5">
                      Keep instructions under {FEEDBACK_MAX_CHARS} characters — this is one slide, not a lesson.
                    </p>
                  )}

                  {/* Opt-in image generation for this slide */}
                  <label className="mt-4 flex items-start gap-2.5 p-3 rounded-xl border border-gray-200 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors">
                    <input
                      type="checkbox"
                      checked={regenWithImages}
                      onChange={(e) => setRegenWithImages(e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-gray-300 text-brand-purple-600 focus:ring-brand-purple-500 flex-shrink-0"
                    />
                    <span className="min-w-0">
                      <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-800">
                        <Image className="w-3.5 h-3.5 text-brand-purple-600" />
                        Generate images for this slide
                      </span>
                      <span className="block text-[11px] text-gray-500 mt-0.5">
                        Off: existing images are kept as they are. On: a new image is generated for
                        this slide, which adds time to the {aiVerb.toLowerCase()}.
                      </span>
                    </span>
                  </label>
                </div>
                <div className="flex items-center justify-end gap-2 px-6 py-4 mt-2 bg-gray-50 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => { setShowRegenDialog(false); setRegenFeedback(''); setRegenWithImages(false); }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleRegenerateSceneContent}
                    disabled={isFeedbackTooLong}
                    title={isFeedbackTooLong ? `Feedback must be ${FEEDBACK_MAX_CHARS} characters or fewer` : undefined}
                    className="px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-brand-purple-500 to-brand-indigo-600 hover:from-brand-purple-600 hover:to-brand-indigo-700 rounded-lg flex items-center gap-2 shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Sparkles className="w-4 h-4" /> {aiVerb}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Error Popup */}
      {processingStep === 'error' && processingError && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[60] p-4" dir="ltr" style={{ direction: 'ltr', textAlign: 'left' }}>
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6 text-center" style={{ color: '#111827' }}>
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#fef2f2' }}>
              <AlertCircle className="w-7 h-7 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Something went wrong</h3>
            <p className="text-sm text-gray-500">{processingError}</p>
            <button
              onClick={() => { setProcessingError(''); setProcessingStep('saving'); }}
              className="mt-4 w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Unsaved Changes Warning */}
      <ConfirmationModal
        isOpen={showUnsavedWarning}
        type="warning"
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to close?"
        confirmText="Discard Changes"
        cancelText="Keep Editing"
        onConfirm={confirmClose}
        onCancel={() => setShowUnsavedWarning(false)}
      />
    </>
  );
}
